import React from "react";
import classnames from "classnames";
import moment from "moment";
import {makeApolloClientCall} from "../../../Api/makeApolloApiCall";

export const isRecommendedSample = ({classification, recommendedUniverse}) => {
    const RECOMMENDED_SAMPLE_UNIVERSES = ['REGIONAL_GROCERY', 'NATURAL_ENHANCED'];
    return classification === 'CHAIN'
        && recommendedUniverse?.some(universe => RECOMMENDED_SAMPLE_UNIVERSES.includes(universe));
};

export const normalizeValue = (value) => value?.replace(/_/g, ' ').toLowerCase();

const UNIVERSE_NAMES = {
    NATURAL_ENHANCED: 'natural enhanced',
    REGIONAL_GROCERY: 'regional grocery',
    INFRA: 'infra',
    C_AND_S: 'c and s',
    CIRCANA_PET: 'circana pet',
    SPINS_PET: 'spins pet',
};

export const toUniverseName = (universe) => UNIVERSE_NAMES[universe] ?? universe;

export const fromUniverseName = (name) =>
    Object.keys(UNIVERSE_NAMES).find(universe => UNIVERSE_NAMES[universe] === name) ?? name;

export const formatRecommendedUniverse = (recommendedUniverse) =>
    Array.isArray(recommendedUniverse)
        ? recommendedUniverse.join(', ')
        : recommendedUniverse ?? '';

export const toUniverseValues = (universe) =>
    Array.isArray(universe) ? universe : (universe ? [universe] : []);

export const toUniverseOption = (universe) => ({
    label: universe.replaceAll('_', ' '),
    value: universe,
});

export const buildInflationFactorMap = (inflationFactors) =>
    inflationFactors.reduce((acc, {universe, storeClassification, inflationFactor}) => {
        acc[`${universe}|${storeClassification}`] = inflationFactor;
        return acc;
    }, {});

export const lookupInflationFactor = (inflationFactorMap, recommendedUniverse, classification) => {
    const universe = normalizeValue(
        Array.isArray(recommendedUniverse) ? recommendedUniverse[0] : recommendedUniverse
    );
    const storeClassification = normalizeValue(classification);

    return inflationFactorMap[`${universe}|${storeClassification}`]
        ?? inflationFactorMap[`${universe}|default`]
        ?? inflationFactorMap[`default|${storeClassification}`]
        ?? inflationFactorMap['default|default'];
};

const OPERATOR_PREDICATES = {
    '>': (cmp) => cmp > 0,
    '>=': (cmp) => cmp >= 0,
    '<': (cmp) => cmp < 0,
    '<=': (cmp) => cmp <= 0,
    '=': (cmp) => cmp === 0,
};

export const parseNumberOperand = (raw) => {
    const numeric = Number(raw);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
};

const isValidYmd = (value) => moment(value, 'YYYY-MM-DD', true).isValid();

export const parseDateOperand = (raw) => (isValidYmd(raw) ? raw : undefined);

export const parseFilterExpression = (input, parseOperand) => {
    if (!input || !input.trim()) return {status: 'empty'};
    const clauses = input.split(/\band\b/i).map(clause => clause.trim()).filter(Boolean);
    if (clauses.length === 0) return {status: 'empty'};

    const parsed = [];
    for (const clause of clauses) {
        const match = clause.match(/^(>=|<=|>|<|=)?\s*(.+)$/);
        const operand = match ? parseOperand(match[2].trim()) : undefined;
        if (operand === undefined) return {status: 'invalid'};
        parsed.push({op: match[1] || '=', operand});
    }
    return {status: 'ok', clauses: parsed};
};

const matchesAllClauses = (value, clauses, compare) =>
    clauses.every(({op, operand}) => OPERATOR_PREDICATES[op](compare(value, operand)));

const numericCompare = (value, operand) => Math.sign(value - operand);
const lexicalCompare = (value, operand) => (value < operand ? -1 : value > operand ? 1 : 0);

export const numberExpressionFilter = (row, columnId, filterValue) => {
    const parsed = parseFilterExpression(filterValue, parseNumberOperand);
    if (parsed.status === 'empty') return true;
    if (parsed.status === 'invalid') return false;

    const value = row.getValue(columnId);
    if (value === '' || value === null || value === undefined) return false;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return false;

    return matchesAllClauses(numeric, parsed.clauses, numericCompare);
};

export const dateExpressionFilter = (row, columnId, filterValue) => {
    const parsed = parseFilterExpression(filterValue, parseDateOperand);
    if (parsed.status === 'empty') return true;
    if (parsed.status === 'invalid') return false;

    const value = row.getValue(columnId);
    if (!isValidYmd(value)) return false;

    return matchesAllClauses(value, parsed.clauses, lexicalCompare);
};

export function ExpressionColumnFilter({column, parseOperand, placeholder}) {
    const value = column.getFilterValue() ?? '';
    const isInvalid = parseFilterExpression(value, parseOperand).status === 'invalid';
    return (
        <input type="text"
               className={classnames('expression-filter', {'expression-filter--invalid': isInvalid})}
               placeholder={placeholder}
               title={isInvalid ? 'Examples: "> 30 and < 35", ">= 10"' : undefined}
               data-testid={`${column.id}-filter-input`}
               value={value}
               onChange={e => column.setFilterValue(e.target.value || undefined)} />
    );
}

export const NumberExpressionColumnFilter = ({column}) =>
    <ExpressionColumnFilter column={column} parseOperand={parseNumberOperand} placeholder="e.g. > 30 and <= 35" />;

export const DateExpressionColumnFilter = ({column}) =>
    <ExpressionColumnFilter column={column} parseOperand={parseDateOperand} placeholder="e.g. >= 2024-01-01" />;

export const NUMBER_EXPRESSION_COLUMN = {filter: NumberExpressionColumnFilter, filterFn: numberExpressionFilter};
export const DATE_EXPRESSION_COLUMN = {filter: DateExpressionColumnFilter, filterFn: dateExpressionFilter};

const collectUniverseOptionsFromItems = (items) => {
    const names = new Set();
    items?.forEach((item) => {
        toUniverseValues(item.universe).forEach((universe) => names.add(universe));
        toUniverseValues(item.recommendedUniverse).forEach((universe) => names.add(universe));
    });
    return [...names].sort().map(toUniverseOption);
};

const projectionUniversesGraphqlQuery = (version) => ` query {
            projectionUniverses(version: "${version}")
        } `;

const universeMembershipGraphqlQuery = (version) => ` query {
            storeUniverse(sourceFilter: SPINS, version: "${version}", limit: 2000) {
                items { universe recommendedUniverse }
            }
        } `;

export const loadApprovedUniverseOptions = (version, apolloCall = makeApolloClientCall) =>
    apolloCall(projectionUniversesGraphqlQuery(version))
        .then((response) =>
            (response.projectionUniverses ?? []).map(toUniverseOption),
        )
        .catch(() =>
            apolloCall(universeMembershipGraphqlQuery(version)).then((response) =>
                collectUniverseOptionsFromItems(response.storeUniverse?.items),
            ),
        )
        .catch(() => []);
